import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MemberPayroll } from "../components/team/MemberPayroll";
import { MemberProfileCard } from "../components/team/MemberProfileCard";
import { MemberAssignments } from "../components/team/MemberAssignments";
import { MemberWorklog } from "../components/team/MemberWorklog";
import { useTeamDashboard, useTeamMemberDetail } from "../hooks/useTeam";
import { euro, shortDate } from "../lib/team";
import { Avatar } from "./TeamPage";
import "../styles/team.css";

/**
 * Mitarbeiterkonto — ein Jahr, vier getrennte Sichten.
 *
 * Vorher standen Stunden, Lohnkonto und Stammdaten untereinander: bei einem
 * Helfer mit vielen Einsätzen lag das Lohnkonto drei Bildschirme tief, und die
 * Stammdaten (IBAN, Telefon) waren dauerhaft sichtbar, obwohl man sie selten
 * braucht. Die Reiter machen jede Sicht einen Klick weit — und der Jahreswähler
 * sitzt jetzt EINMAL im Kopf und gilt für alles, statt nur für die Stunden.
 */
const REITER = [
  { id: "einsaetze", label: "Einsätze" },
  { id: "stunden", label: "Stunden & Pauschalen", kurz: "Stunden" },
  { id: "lohnkonto", label: "Lohnkonto" },
  { id: "stammdaten", label: "Stammdaten", kurz: "Daten" },
] as const;

type ReiterId = (typeof REITER)[number]["id"];

export default function TeamMemberPage() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  // Reiter und Jahr stehen in der Adresse: ein Reload landet dort, wo man war,
  // und ein Link auf "Lohnkonto 2025" lässt sich weitergeben.
  const [suchParams, setSuchParams] = useSearchParams();
  const jahrParam = Number(suchParams.get("jahr"));
  const year = jahrParam >= 2000 && jahrParam <= 2100 ? jahrParam : new Date().getFullYear();
  const reiter = (REITER.find((r) => r.id === suchParams.get("reiter"))?.id ?? "einsaetze") as ReiterId;
  const setzeParam = (key: string, wert: string) => {
    const naechste = new URLSearchParams(suchParams);
    naechste.set(key, wert);
    setSuchParams(naechste, { replace: true });
  };

  const detail = useTeamMemberDetail(memberId, year);
  const dashboard = useTeamDashboard();

  if (detail.isLoading) return <div className="team-page"><div className="team-state large"><p>Mitarbeiterkonto wird geladen …</p></div></div>;
  if (detail.isError || !detail.data) return <div className="team-page"><button className="team-back" onClick={() => navigate("/team")}>← Zurück zum Team</button><div className="team-state large"><p>Das Mitarbeiterkonto konnte nicht geladen werden.</p><button className="team-button secondary" onClick={() => detail.refetch()}>Erneut versuchen</button></div></div>;

  const { member, payroll, assignments, entries, pauschalen, payouts } = detail.data;
  const jetzt = new Date().getFullYear();
  const stundenAnzahl = entries.length + pauschalen.length;
  const offeneStunden = entries.filter((e) => ["geplant", "pending_helfer", "pending_inhaber"].includes(e.confirm_status)).length;

  return (
    <div className="team-page team-detail-page">
      <button type="button" className="team-back" onClick={() => navigate("/team")}>← Zurück zum Team</button>

      <header className="team-member-head compact">
        <div className="team-member-identity">
          <Avatar name={member.name} />
          <div>
            <div className="team-member-title">
              <h1>{member.name}</h1>
              <span className={`team-pill ${member.active ? "success" : "muted"}`}>{member.active ? "Aktiv" : "Inaktiv"}</span>
            </div>
            <p>{member.role || "Helfer"} · {euro(member.hourly_rate)}/Std. · im Team seit {shortDate(member.created_at)}</p>
          </div>
        </div>
        <div className="team-head-tools">
          {/* Ein Jahreswähler für die ganze Seite. Stunden, Auszahlungen und
              Belege zeigten vorher unterschiedliche Zeiträume nebeneinander. */}
          <div className="team-year-nav">
            <button type="button" aria-label="Vorjahr" onClick={() => setzeParam("jahr", String(year - 1))}>‹</button>
            <strong>{year}</strong>
            <button type="button" aria-label="Folgejahr" disabled={year >= jetzt} onClick={() => setzeParam("jahr", String(year + 1))}>›</button>
          </div>
        </div>
      </header>

      {/* Seit 20.08.2026 holt sich der Helfer seinen Zugang selbst: er tippt auf
          team.vimaanagarten.de seine Mailadresse ein, und wer hier aktiv mit
          Adresse steht, bekommt den Link. Der frühere Knopf „Zugangslink" ist
          deshalb weg — diese Zeile sagt nur noch, ob es geklappt hat. */}
      <p className="team-app-state-line">
        <span className={member.app_status === "aktiv" ? "online" : ""} />
        {member.app_status === "aktiv"
          ? `Nutzt die Team-App · zuletzt ${shortDate(member.app_zuletzt || "")}`
          : !member.email
            ? "Ohne E-Mail kein Zugang zur Team-App — trag eine ein, dann kann er sich selbst anmelden"
            : member.app_status === "eingeladen"
              ? "Zugang eingerichtet, aber noch nie angemeldet — vielleicht kurz nachfragen"
              : "Noch nicht angemeldet. Er meldet sich selbst an, unter team.vimaanagarten.de"}
      </p>

      <div className="team-detail-kpis">
        <article><span>Offener Saldo</span><strong className="money-positive">{euro(payroll.saldo_offen)}</strong></article>
        <article><span>Verdient {year}</span><strong>{euro(payroll.verdient_jahr)}</strong></article>
        <article><span>Verdient gesamt</span><strong>{euro(payroll.verdient_gesamt)}</strong></article>
        <article><span>Ausgezahlt gesamt</span><strong>{euro(payroll.ausgezahlt_gesamt)}</strong></article>
      </div>

      <nav className="team-tabs" role="tablist">
        {REITER.map((r) => (
          <button
            key={r.id}
            type="button"
            role="tab"
            aria-selected={reiter === r.id}
            className={reiter === r.id ? "aktiv" : ""}
            onClick={() => setzeParam("reiter", r.id)}
          >
            {"kurz" in r ? <><span className="team-tab-lang">{r.label}</span><span className="team-tab-kurz">{r.kurz}</span></> : r.label}
            {r.id === "einsaetze" && assignments.length > 0 && <em>{assignments.length}</em>}
            {r.id === "einsaetze" && assignments.some((e) => e.einladungs_status === "wartet_auf_helfer" || e.einladungs_status === "wartet_auf_inhaber") && <em className="warnung">offen</em>}
            {r.id === "stunden" && stundenAnzahl > 0 && <em>{stundenAnzahl}</em>}
            {r.id === "stunden" && offeneStunden > 0 && <em className="warnung" title={`${offeneStunden} offen`}>{offeneStunden} offen</em>}
            {r.id === "lohnkonto" && payouts.length > 0 && <em>{payouts.length}</em>}
          </button>
        ))}
      </nav>

      <div className="team-tab-inhalt">
        {reiter === "einsaetze" && <MemberAssignments assignments={assignments} />}
        {reiter === "stunden" && (
          <MemberWorklog member={member} year={year} entries={entries} flatRates={pauschalen} />
        )}
        {reiter === "lohnkonto" && (
          <MemberPayroll
            memberId={member.id}
            memberName={member.name}
            year={year}
            payroll={payroll}
            payouts={payouts}
            owners={dashboard.data?.owners ?? []}
          />
        )}
        {reiter === "stammdaten" && <MemberProfileCard member={member} />}
      </div>
    </div>
  );
}
